import React, { useCallback, useEffect, useState } from 'react';
import { noop, partial, times, cloneDeep } from 'lodash';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import RubricBlock from '@dyl/blocks/RubricBlock';
import { Grid, Typography, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AudioAndTranscriptBlock from "@dyl/blocks/AudioAndTranscriptBlock";
import ImagePicker from "@dyl/components/ImagePicker";
import indexToLetter from "@dyl/utils/indexToLetter";
import { useImage } from "@dyl/systems/ImagesManager";
import DragCanvas, { DragCanvasImage, DragCanvasText } from "@dyl/components/DragCanvas";
import SectionDividers from "@dyl/components/SectionDividers";
import TaskTemplateSelector from '../../../blocks/TaskRadioOptionBlockForPictureEditing';

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: theme.breakpoints.values.md,
  },
  pickersContainer: {
    maxWidth: theme.breakpoints.values.md,
  },
  groupHeader: {
    marginBottom: '16px',
    textAlign: 'center'
  },
  fullWidthBlock: {
    width: '100%',
    '& .MuiGrid-root': {
      width: '100%'
    }
  },
}));

export default function PictureEditingEditor() {
  const classes = useStyles();
  const [items] = useTaskField('items');

  return (
    <div className={classes.root}>
      <SectionDividers>
        {times(items.length, i => <Item key={i} index={i} />)}
      </SectionDividers>
    </div>
  );
}

function Item({ index }) {
  const classes = useStyles();
  const useField = partial(useTaskField, 'items', index);
  const [isExample] = useField('is_example');
  const forceImage = index <= 1;
  const [phase, setPhase] = useField('phase');
  const finalTemplate = forceImage ? 'image' : (phase === 'PHASE_IMAGE' ? 'image' : phase === 'PHASE_TEXT' ? 'text' : null);

  const [audioId, setAudioId, audioError] = useField('audio_id');
  const [answers, setAnswers] = useField('answers');
  const [phaseAnswerTemplates] = useField('phase_answer_templates');

  useEffect(() => {
    if (forceImage) return;
    if (!phase) return;
    if (answers && answers.length > 0) return;

    const phaseKey = phase;
    const template = phaseAnswerTemplates?.[phaseKey];
    if (template) {
      const cloned = cloneDeep(template);

      cloned.forEach((ans, i) => {
        // initialize coordinates for both image/text to defaults
        ans.coordinates = { top: 0, left: 0 };
      });

      setAnswers(cloned);
      const currentPhaseKey = phaseKey;
      if (phase !== currentPhaseKey) {
        setPhase(currentPhaseKey);
      }
    }
  }, [phase, answers, phaseAnswerTemplates, forceImage, setAnswers, setPhase]);

  const [items] = useTaskField('items');
  const examplesUntilNow = items
    .slice(0, index)
    .filter(x => x.is_example)
    .reduce(x => x + 1, 0);

  return (
    <>
      <Typography variant='h6'>
        {isExample ? `Example` : `Item ${index + 1 - examplesUntilNow}`}
      </Typography>

      {index >= 2 && (
        <TaskTemplateSelector
          index={index}
          selectedTemplate={phase === 'PHASE_IMAGE' ? 'image' : phase === 'PHASE_TEXT' ? 'text' : ''}
          onChange={(idx, value) => {
            const p = value === 'image' ? 'PHASE_IMAGE' : value === 'text' ? 'PHASE_TEXT' : null;
            setPhase(p);
          }}
        />
      )}

  {(forceImage || phase) && (
        <>
          <Grid container spacing={0}>
            <Grid item xs={12} sm={8}>
              {isExample && (
                <Grid item xs={12}>
                  <RubricBlock
                    index={index}
                    fullWidth
                  />
                </Grid>
              )}

              <AudioAndTranscriptBlock
                value={audioId ?? ''}
                onChange={id => setAudioId(id)}
                label='Stimulus Audio'
                error={audioError}
                required
                fullWidth
              />
            </Grid>

            {finalTemplate === 'image'
              ? <PhaseImagePlacementEditor index={index} />
              : <PhaseTextPlacementEditor index={index} />
            }
          </Grid>
        </>
      )}
    </>
  );
}

function PhaseImagePlacementEditor({ index, draggable = true, backgroundImagePath = 'background_image' }) {
  const [backgroundImageId] = useTaskField(backgroundImagePath);
      const { src: backgroundImageSrc } = useImage(backgroundImageId);
      const [answers] = useTaskField('items', index, 'answers');
      const [shownImage, setShownImage] = useState(0);

      const setShown = useCallback((index) => () => {
          if (index === 0 || answers[index].image_id !== null) { setShownImage(index); }
      }, [answers]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant='h6'>Images</Typography>
      </Grid>
      <P1ImagePicker
        item md={4}
        onChange={setShown(0)}
        label={'Key'}
        path={['items', index, 'answers', 0, 'image_id']}
      />
      <P1ImagePicker
        item md={4}
        onChange={setShown(0)}
        onMouseOut={setShown(0)}
        onMouseOver={setShown(1)}
        label={'Distractor 1'}
        path={['items', index, 'answers', 1, 'image_id']}
      />
      <P1ImagePicker
        item md={4}
        onChange={setShown(0)}
        onMouseOut={setShown(0)}
        onMouseOver={setShown(2)}
        label={'Distractor 2'}
        path={['items', index, 'answers', 2, 'image_id']}
      />

      <Grid style={{ marginTop: 4, marginBottom: 6, }} />
      <Grid item xs={12}>
        <Typography variant='h6'>Positions</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant='body1'>
          Note: The shape with the solid outline (black) is the key position, the dashed outlines (red) are the distractor positions.
        </Typography>
      </Grid>

      <Grid item md={12}>
        <DragCanvas backgroundSrc={backgroundImageSrc}>

          {times(answers.length, answerIndex => (
            <AnswerDraggableBoundingBox
              key={answerIndex}
              index={answerIndex}
              itemIndex={index}
              disabled={!draggable}
              imagePath={['items', index, 'answers', shownImage, 'image_id']}
            />
          ))}
        </DragCanvas>
      </Grid>
    </Grid>
  );
}

  function P1ImagePicker({ label, path, onChange = noop, ...props }) {
      const [imageId, _setImageId, imageError] = useTaskField(...path);
      const setImageId = useCallback((...args) => {
          _setImageId(...args);
          onChange(...args);
      }, []);
      return (
          <Grid {...props}>
              <ImagePicker
                  fullWidth
                  label={label}
                  value={imageId ?? ''}
                  onChange={setImageId}
                  error={!!imageError}
              />
          </Grid>
      );
  }

  function AnswerDraggableBoundingBox({ index, itemIndex, disabled = false, imagePath = null }) {
      imagePath = imagePath || ['items', itemIndex, 'answers', index, 'image_id'];
      const [imageId] = useTaskField(...imagePath);
      const { src } = useImage(imageId);

      const useField = partial(useTaskField, 'items', itemIndex, 'answers', index);
      const [isCorrectAnswer] = useField('is_correct_answer');
      const [coordinates, setCoordinates] = useField('coordinates');

      return (
          <DragCanvasImage
              text={indexToLetter(index)}
              disabled={disabled}
              src={src}
              alt={imageId}
              coordinates={coordinates}
              onCoordinatesChange={setCoordinates}
              imageStyle = {{
                  opacity: 0.75,
                  outlineColor: isCorrectAnswer ? 'black' : 'red',
                  outlineStyle: isCorrectAnswer ? 'solid' : 'dashed',
                  outlineWidth: '4px',
                  borderRadius: '15px'
              }}
          />
      );
  }

function AnswerDraggableText({ index, itemIndex, textPath, disabled = false }) {
  const useField = partial(useTaskField, 'items', itemIndex, 'answers', index);
  const [isCorrectAnswer] = useField('is_correct_answer');
  const [coordinates, setCoordinates] = useField('coordinates');
  const [displayText] = useTaskField(...textPath);
  const letter = indexToLetter(index);

  return (
    <DragCanvasText
      disabled={disabled}
      text={displayText}
      coordinates={coordinates}
      onCoordinatesChange={setCoordinates}
      style={{
        outlineColor: isCorrectAnswer ? 'black' : 'red',
        outlineStyle: isCorrectAnswer ? 'solid' : 'dashed',
        backgroundColor: 'white',
        opacity: 1,
        borderRadius: '30px',
        minWidth: '120px',
        minHeight: '48px',
        boxSizing: 'border-box',
        fontSize: '22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        letter,
      }}
    />
  );
}

function PhaseTextPlacementEditor({ index, draggable = true, backgroundImagePath = 'background_image' }) {
  const [backgroundImageId] = useTaskField(backgroundImagePath);
  const { src: backgroundImageSrc } = useImage(backgroundImageId);
  const [answers] = useTaskField('items', index, 'answers');
  const [text1, setText1] = useTaskField('items', index, 'answers', 0, 'text');
  const [text2, setText2] = useTaskField('items', index, 'answers', 1, 'text');
  const [text3, setText3] = useTaskField('items', index, 'answers', 2, 'text');
  const [shownText, setShownText] = useState(0);

  const onText1Change = useCallback((e) => {
    const value = e.target.value.slice(0, 11);
    setText1(value);
  }, [setText1]);

  const onText2Change = useCallback((e) => {
    const value = e.target.value.slice(0, 11);
    setText2(value);
  }, [setText2]);

  const onText3Change = useCallback((e) => {
    const value = e.target.value.slice(0, 11);
    setText3(value);
  }, [setText3]);

  const setShown = useCallback((i) => () => {
   if (i === 0 || [text1, text2, text3][i]?.trim()) {
     setShownText(i);
   }
 }, [text1, text2, text3]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant='h6'>Words</Typography>
      </Grid>

      <Grid item md={4}>
        <TextField
          label='Key'
          value={text1}
          onChange={onText1Change}
          variant='filled'
          fullWidth
          onMouseOver={setShown(0)}
          onMouseOut={setShown(0)}
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          label='Distractor 1'
          value={text2}
          onChange={onText2Change}
          variant='filled'
          fullWidth
          onMouseOver={setShown(1)}
          onMouseOut={setShown(0)}
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          label='Distractor 2'
          value={text3}
          onChange={onText3Change}
          variant='filled'
          fullWidth
          onMouseOver={setShown(2)}
          onMouseOut={setShown(0)}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant='h6'>Positions</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant='body1'>
          Note: The shape with the solid outline (black) is the key position, the dashed outlines (red) are the distractor positions.
        </Typography>
      </Grid>

      <Grid item md={12}>
        <DragCanvas backgroundSrc={backgroundImageSrc}>
          {text1 && text1.trim() !== '' &&
            times(answers.length, answerIndex => (
              <AnswerDraggableText
                key={answerIndex}
                index={answerIndex}
                itemIndex={index}
                disabled={!draggable}
                textPath={['items', index, 'answers', shownText, 'text']}
              />
            ))
          }
        </DragCanvas>
      </Grid>
    </Grid>
  );
}
